import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useT } from '../i18n';
import NotificationCenter from './NotificationCenter';
import CommandPalette from './CommandPalette';
import useTheme from '../hooks/useTheme';
import api from '../api';
import { tx as __tx } from "../lib/tx";

const PATH_NAMES = {
  '/': 'overview',
  '/sites': 'sites',
  '/audit': 'audit',
  '/keywords': 'keywords',
  '/ranks': 'ranks',
  '/content': 'content',
  '/articles': 'articles',
  '/pbn': 'pbn',
  '/promotion': 'promotion',
  '/automation': 'automation',
  '/google-setup': 'google-setup',
  '/reports': 'reports',
  '/settings': 'settings',
};

const isTextInput = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
};

export default function TopBar({ pageKey }) {
  const t = useT();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [status, setStatus] = useState('checking'); // checking | live | offline

  const segment = pageKey || PATH_NAMES[location.pathname] ||
    (location.pathname.replace('/', '') || 'overview');

  // Global ⌘K / Ctrl+K to open command palette
  useEffect(() => {
    const handler = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Slash shortcut to focus palette (only when not typing)
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== '/' || isTextInput(document.activeElement)) return;
      e.preventDefault();
      setPaletteOpen(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Poll scheduler health for LIVE pulse
  useEffect(() => {
    let cancelled = false;
    const ping = () => {
      api.get('/scheduler/status', { timeout: 5000 })
        .then(() => { if (!cancelled) setStatus('live'); })
        .catch(() => { if (!cancelled) setStatus('offline'); });
    };
    ping();
    const id = setInterval(ping, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const statusLabel = status === 'live'
    ? (t.top?.live || 'live')
    : status === 'offline'
      ? (t.top?.offline || 'offline')
      : (t.top?.checking || 'checking');

  return (
    <div className="xp-top">
      <div className="xp-crumb">
        <a href="https://xpnsr.tech" target="_blank" rel="noreferrer">{__tx("xpnsr")}</a>
        <span className="xp-crumb-sep">/</span>
        <Link to="/"><b>SEO</b></Link>
        <span className="xp-crumb-sep">/</span>
        <span>{segment}</span>
      </div>

      <div className="xp-top-search" role="search">
        <button
          type="button"
          className="xp-top-search-trigger"
          onClick={() => setPaletteOpen(true)}
          aria-label={t.top?.searchPlaceholder || 'Search'}
          aria-keyshortcuts="Control+K Meta+K"
          data-testid="topbar-search-trigger"
        >
          {t.top?.searchPlaceholder || 'Search…'}
        </button>
        <span className="xp-top-kbd" aria-hidden="true">⌘K</span>
      </div>

      <div
        className="xp-pulse"
        data-status={status}
        aria-live="polite"
        title={status === 'live'
          ? 'Scheduler reachable'
          : status === 'offline'
            ? 'Scheduler unreachable — background jobs may be paused'
            : 'Checking scheduler…'}
      >
        <span className="xp-pulse-dot" aria-hidden="true" />
        <span>{statusLabel}</span>
      </div>

      <button
        type="button"
        onClick={toggleTheme}
        className="xp-top-theme"
        data-testid="theme-toggle"
        title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
        aria-label={theme === 'dark' ? 'Switch to Light theme' : 'Switch to Dark theme'}
      >
        {theme === 'dark'
          ? <Sun size={14} aria-hidden="true" />
          : <Moon size={14} aria-hidden="true" />}
      </button>

      <div className="xp-top-notif">
        <NotificationCenter />
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
